import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, Phone, Star, ShieldCheck, Clock } from "lucide-react";
import { localAreas } from "@/routes/areas.local.$area";
import { getBusinessConfig } from "@/lib/site-config";
import { seoHead, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { PrimaryCTA } from "@/components/PrimaryCTA";

const NEAR_ME_FAQS = [
  {
    q: "What's the closest MOT centre to Canary Wharf?",
    a: "Woodseer Autos is roughly an 8-minute drive from One Canada Square via Aspen Way. We're DVSA-authorised for classes 1, 2, 3, 4, 5 and 7 — drop the car off before work, collect after.",
  },
  {
    q: "Are you walkable from any DLR station?",
    a: "Yes — we're a 5-minute walk from Westferry DLR and Limehouse station, and a short walk from Poplar DLR. Many customers drop the car off and take the train into work.",
  },
  {
    q: "Which East London postcodes do you cover?",
    a: "Most of E1, E2, E3, E14 and parts of E16. That includes Poplar, Canary Wharf, Isle of Dogs, Limehouse, Mile End, Bow, Bethnal Green, Whitechapel, Wapping, Stratford and the wider Docklands.",
  },
  {
    q: "Are you open on Saturdays?",
    a: "Yes — Monday to Saturday 9am to 6pm. Saturdays book up fast for MOT slots so call ahead or use the booking form.",
  },
  {
    q: "Do you offer collection from my address?",
    a: "Sometimes — depending on our diary that day and where you are within E14/E1/E3. Call us with your postcode and we'll let you know honestly.",
  },
];

export const Route = createFileRoute("/garage-near-me")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/garage-near-me",
      title: "Garage Near Me East London | MOT, Service, Repair | Woodseer Autos",
      description:
        "Looking for a trusted garage near you in East London? Woodseer Autos covers Poplar, Canary Wharf, Isle of Dogs, Limehouse, Mile End, Bow, Stratford and the wider Docklands. DVSA-authorised, 4.4★ rated.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Garage Near Me", path: "/garage-near-me" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        faqSchema(NEAR_ME_FAQS) as Record<string, unknown>,
      ],
    }),
  component: GarageNearMePage,
});

function GarageNearMePage() {
  const biz = getBusinessConfig();
  return (
    <>
      <section className="py-14 md:py-20 bg-secondary">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Local · East London</p>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold text-foreground tracking-tight">Garage near you in East London</h1>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed">
            Search for a trusted local garage and you'll find dozens of options. Here's what makes us different — and how to reach us from each East London neighbourhood.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-3 py-1 font-bold"><ShieldCheck className="h-3.5 w-3.5" /> DVSA V106376</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-3 py-1 font-bold"><Star className="h-3.5 w-3.5" /> 4.4★ on Google</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-3 py-1 font-bold"><Clock className="h-3.5 w-3.5" /> Mon–Sat 9–6</span>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="text-2xl font-bold text-foreground text-center">Pick your area</h2>
          <p className="mt-2 text-center text-sm text-muted-foreground">Each page gives directions, travel time and a local FAQ.</p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {localAreas.map((a) => (
              <Link
                key={a.slug}
                to="/areas/local/$area"
                params={{ area: a.slug }}
                className="rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:bg-primary/[0.03] transition-colors"
              >
                <div className="flex items-center gap-2 text-primary text-[10px] font-bold uppercase tracking-wider">
                  <MapPin className="h-3 w-3" /> {a.postcode}
                </div>
                <p className="mt-1 text-base font-bold text-foreground">{a.name}</p>
                <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{a.travelTime}</p>
              </Link>
            ))}
          </div>

          <div className="mt-10 rounded-2xl border border-border bg-card p-6 text-center">
            <p className="text-sm text-muted-foreground">Not sure which area applies to you? Call us with your postcode — we'll tell you the fastest route in.</p>
            <div className="mt-2 flex justify-center">
              <PrimaryCTA location="garage-near-me-strip" intent="local MOT or quote" variant="light" />
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={NEAR_ME_FAQS} title="Near-me FAQs" />
    </>
  );
}
